/* eslint-disable no-unused-vars */
import * as d3 from 'd3'
import moment from 'moment'

import { ChartView } from '../ChartView'

import { getByteRound } from '../unit'

/**
 * Class which represents a TimeChart model used in the TimeChart vue component
 */
export class TimeChartView extends ChartView {
  constructor(chartId, dataset, chartType, width, height, margin) {
    super(chartId, dataset, chartType, width, height, margin)

    this.legendOptions = {
      rectSize: 12,
      spacing: 5,
      textSize: 11,
    }

    this.axisDateFormat = this.$locale == 'en' ? '%m/%d/%Y' : '%d/%m/%Y'
    this.weekEnds = []
  }

  /**
   * Draw a time chart according to the retrieved dataset
   */
  draw() {
    super.draw()

    // text label for the y axis
    this.svg
      .append('text')
      .attr('id', 'y-axis-title')
      .attr('class', 'title')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - this.margin.left / 1.2)
      .attr('x', 0 - this.height / 2)
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .text(this.yTitle)

    this.createChart()

    //Setting chart size for further needs (like the calculation x axis ticks spacing)
    this.parentSize = this.g.node().getBBox()

    d3.selectAll('#svg-' + this.chartId + ' .mouse-informations').style('opacity', '0')
  }

  /**
   * Create chart according to his settings and retrieved dataset
   */
  createChart() {
    super.createChart()

    //Clearing previous displayed chart
    this.svg.select('#timechart-' + this.chartId).remove()
    this.g = this.svg.append('g').attr('id', 'timechart-' + this.chartId)

    //Display legends if needed
    this.createLegends()

    //Format date and value for d3 scaling process
    this.dataset.lines.forEach(line => {
      line.items.forEach(d => {
        d.date = moment(d.date, 'YYYY-MM-DD')
        if (String(d.value).indexOf('.') > -1) {
          d.value = parseFloat(d.value)
        }
      })
    })

    this.dataset.items.forEach(item => {
      item.date = moment(item.date, 'YYYY-MM-DD')
    })

    this.sortedDataset.lines.forEach(line => {
      line.items.forEach(d => {
        d.date = moment(d.date, 'YYYY-MM-DD')
        if (String(d.value).indexOf('.') > -1) {
          d.value = parseFloat(d.value)
        }

        if (
          this.dataset.unit === 'byte' ||
          this.dataset.dataValue === 'max_memory_level_gradient'
        ) {
          d.value = getByteRound(d.value)
        } else if (this.dataset.dataValue === 'min_level_gradient') {
          d.value = Math.abs(d.value)
        }
      })
    })

    //Display chart in stacked mode or not
    if (this.isStackedChart()) {
      this.createStackedChart()
    } else {
      this.createLineChart()
    }

    //Display grid if needed
    this.displayGrid()

    //Display mouse over line and tooltip
    if (this.mode == 'normal') {
      this.createMouseOverLine()
      this.updateTimeCharts()
    }
  }

  /**
   * Create a vertical line according to the on over mouse pointer position
   */
  createMouseOverLine() {
    let that = this
    let rectPadding = 3

    //Focused data
    this.focusedDateRect = this.svg
      .append('rect')
      .attr('class', 'focused-date-rect')
      .attr('x', this.width / 2)
      .attr('y', -3)
      .attr('width', 0)
      .attr('height', 0)
      .style('fill', '#243d49')
      .style('opacity', '0')

    this.focusedDate = this.svg
      .append('text')
      .attr('class', 'focused-date')
      .attr('x', this.width / 2)
      .attr('y', -3)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .style('font-weight', 'bold')
      .attr('fill', 'white')
      .style('opacity', '0.0')

    this.svg.selectAll('.mouse-over-effects').remove()

    this.mouseG = this.svg.append('g').attr('class', 'mouse-over-effects')

    this.mouseG
      .append('path')
      .attr('class', 'mouse-line')
      .style('stroke', 'white')
      .style('stroke-width', '1px')
      .style('opacity', '0')
      .attr('height', that.height)
      .attr('width', that.svgWidth)
      .attr('chart-id', this.chartId)

    //On over rectangle which displays line points values according to the focused date
    this.mouseInformations = this.mouseG
      .append('g')
      .attr('class', 'mouse-informations')
      .style('opacity', '0')
      .attr('chart-id', this.chartId)
      .attr('id', 'mouse-informations-' + this.chartId)

    this.mouseInformations
      .append('rect')
      .attr('class', 'tooltip-rect')
      .attr('width', 100)
      .attr('height', 100)

    this.mouseInformations
      .append('text')
      .attr('transform', 'translate(10, 0)')
      .style('fill', 'white')
      .style('font-size', '9px')
      .style('font-weight', 'bold')
      .attr('class', 'mouse-per-line-text')

    this.mousePerLine = this.mouseG
      .selectAll('.mouse-per-line')
      .data(this.isStackedChart() ? this.stackedData : this.sortedDataset.lines)
      .enter()
      .append('g')
      .attr('class', 'mouse-per-line')
      .attr('chart-id', this.chartId)
      .attr('category', function(d, i) {
        if (that.isStackedChart()) {
          return d[0].data.values[i].category
        } else {
          return d.sort
        }
      })

    this.mousePerLine
      .append('circle')
      .attr('r', 3)
      .style('stroke', function(d, i) {
        return that.getFocusColor(d, i)
      })
      .style('fill', function(d, i) {
        return that.getFocusColor(d, i)
      })
      .style('stroke-width', '1px')
      .style('opacity', '0')

    //Add mouseover event to the main svg element
    this.svg
      .append('rect')
      .attr('class', 'mouse-over-rect')
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .attr('width', this.svgWidth)
      .attr('height', this.height)
      .on('mouseout', this.onInformationsMouseOut)
      .on('mouseover', this.onInformationsMouseOver)
      .on('mousemove', function() {
        let mouse = d3.mouse(this)
        let xDate = that.x.invert(mouse[0])
        let bisect = d3.bisector(function(d) {
          return d.date
        }).right

        //Get index according to mouse position (checking edge case for index 0)
        let idx = mouse[0] <= that.xSpace / 2 ? 0 : bisect(that.dataset.items, xDate)

        if (idx >= that.dataset.items.length) {
          idx = that.dataset.items.length - 1
        }
        // Retrieving x position coordinate according to dataset items : select the closest dataset items
        let xValue = that.x(that.dataset.items[idx].date)

        //Updating vertical line x position according to mouse pointer position
        d3.selectAll('.mouse-line').attr('d', function() {
          let data = 'M' + xValue + ',' + d3.select(this).attr('height')
          data += ' ' + xValue + ',' + 0
          return data
        })

        //Updating on over value informations displayed in the tooltip
        d3.selectAll('.mouse-informations').attr('transform', function() {
          let node = d3.select(this)
          let minYOffset = 10,
            spacing = 20,
            maxHeight = 0,
            maxWidth = 0,
            padding = 5

          //Getting focused time chart for the on over event
          let focusedTimeChart = that.getFocusedTimeChart(node.attr('chart-id'))

          if (!focusedTimeChart) {
            return
          }

          let focusedXValue = focusedTimeChart.x(that.dataset.items[idx].date)

          //Format available data to display in the tooltip
          let total = 0

          let data = focusedTimeChart.sortedDataset.lines.map(line => {
            total += line.items[idx].value
            return {
              date: line.items[idx].date,
              value: line.items[idx].value,
              sort: line.sort,
              sortBy: focusedTimeChart.config.sort_by,
            }
          })

          if (!data || data.length == 0) {
            //If all legends are unchecked...
            data = focusedTimeChart.dataset.lines.map(line => {
              return {
                date: line.items[idx].date,
                value: 0,
                sort: line.sort,
                sortBy: focusedTimeChart.config.sort_by,
              }
            })
          }

          //Format data in percent if needed by chart display options
          if (
            focusedTimeChart.config.display_options &&
            focusedTimeChart.config.display_options.stacked_mode == 'percentage'
          ) {
            data.forEach(item => {
              item.value = Number((item.value * 100) / total).toFixed(1)
            })
            total = 100
          }

          if (
            focusedTimeChart.config.sort_by !== 'all' &&
            focusedTimeChart.sortedDataset.lines.length > 1
          ) {
            data.push({
              date: that.dataset.items[idx].date,
              value: Math.round(total),
              sort: 'total',
              sortBy: 'all',
            })
          }

          data.sort((a, b) => {
            return b.value - a.value
          })

          //Clearing and adding text for all available types of values
          node.selectAll('tspan').remove()

          node
            .select('text')
            .selectAll('.tspan-infos')
            .data(data)
            .enter()
            .append('tspan')
            .text(function(d) {
              return focusedTimeChart.getTooltipText(d.sort, d.value)
            })
            .attr('x', 0)
            .attr('dx', -padding)
            .attr('dy', padding * 2)
            .style('fill', function(d, i) {
              return focusedTimeChart.getInfosLegendColor(d, i)
            })
            .style('font-size', this.labelTextSize)
            .style('font-weight', 'bold')

          //Calculating tooltip position according to the on over event
          node.selectAll('tspan').each(function() {
            let bbox = d3
              .select(this)
              .node()
              .getBBox()
            let yOffset = bbox.y + bbox.height + 3
            let width = bbox.width + padding * 2
            if (maxHeight < yOffset) {
              maxHeight = yOffset
            }
            if (maxWidth < width) {
              maxWidth = width
            }
          })

          //Y Offset
          let yOffset =
            focusedTimeChart.y(focusedTimeChart.isStackedChart() ? total : data[0].value) -
            spacing / 2

          //Checking edge case
          if (yOffset + maxHeight + spacing > that.parentSize.height || yOffset < 0) {
            yOffset = minYOffset
          }

          if ((focusedXValue + maxWidth) * 1.1 > that.parentSize.width) {
            //Add 10% to be sure :D
            focusedXValue = focusedXValue - maxWidth - spacing * 2
          }

          node
            .select('rect')
            .attr('width', maxWidth)
            .attr('height', maxHeight)

          return 'translate(' + (focusedXValue + spacing) + ', ' + yOffset + ')'
        })

        //Updating x position for focused date and background rect on the the top of the chart
        d3.selectAll('.focused-date')
          .attr('x', xValue)
          .text(that.dataset.items[idx].date.format('DD/MM/YYYY'))
          .attr('fill', function() {
            let date = that.dataset.items[idx].date
            if (date.day() == 0 || date.day() == 6) {
              //week end
              return '#dfa579'
            } else {
              return '#feffe9'
            }
          })

        let focusedDateBBox = that.focusedDate.node().getBBox()

        d3.selectAll('.focused-date-rect')
          .attr('x', xValue - (focusedDateBBox.width + rectPadding * 3) / 2)
          .attr('y', focusedDateBBox.y - rectPadding)
          .attr('width', focusedDateBBox.width + rectPadding * 3)
          .attr('height', focusedDateBBox.height + rectPadding * 2)

        d3.selectAll('.mouse-per-line').attr('transform', function(d) {
          let focusedTimeChart = that.getFocusedTimeChart(d3.select(this).attr('chart-id'))

          if (focusedTimeChart) {
            let data = focusedTimeChart.sortedDataset.lines.map(line => {
              return {
                date: line.items[idx].date,
                value: line.items[idx].value,
                sort: line.sort,
                sortBy: focusedTimeChart.config.sort_by,
              }
            })
            if (data.length == 1) {
              d3.select('#mouse-information-' + that.chartId)
            }

            let value, xValue, yValue

            if (focusedTimeChart.isStackedChart()) {
              value = Math.round(d[idx][1] - d[idx][0])
              //let category = d3.select(this).attr("category");
              xValue = that.x(that.dataset.items[idx].date)
              yValue = focusedTimeChart.y(d[idx][1])
            } else {
              value = d.items[idx].value
              xValue = that.x(d.items[idx].date)
              yValue = focusedTimeChart.y(value)
            }

            return 'translate(' + xValue + ',' + yValue + ')'
          } else {
            //Hide other values
            d3.select(this).style('opacity', '0')
            d3.select(this)
              .select('text')
              .style('opacity', '0')
            d3.select(this)
              .select('rect')
              .style('opacity', '0')
            return 'translate(-100, -100)'
          }
        })
      })
  }

  /**
   * Create a multiple lines chart (if needed)
   */
  createLineChart() {
    let that = this

    //Line path
    let valueline = d3
      .line()
      .x(function(d) {
        return that.x(d.date)
      })
      .y(function(d) {
        return that.y(d.value)
      })

    //Area path
    let area = d3
      .area()
      .defined(function(d) {
        return d
      })
      .x(function(d) {
        return that.x(d.date)
      })
      .y0(this.height)
      .y1(function(d) {
        return that.y(d.value)
      })

    //Scaling x and y values
    this.x = d3.scaleTime().range([0, this.width])
    this.y = d3.scaleLinear().range([this.height, 0])

    this.x.domain(
      d3.extent(
        this.sortedDataset.lines && this.sortedDataset.lines.length > 0
          ? this.sortedDataset.lines[0].items
          : this.dataset.lines[0].items,
        function(d) {
          return d.date
        },
      ),
    )

    //Setting y domain values according to zoom mode of the current chart
    if (this.isZoomedChart()) {
      this.y.domain([
        d3.min(
          this.sortedDataset.lines && this.sortedDataset.lines.length > 0
            ? this.sortedDataset.lines
            : this.dataset.lines,
          function(d) {
            return d3.min(d.items, function(d2) {
              return d2.value
            })
          },
        ),
        d3.max(
          this.sortedDataset.lines && this.sortedDataset.lines.length > 0
            ? this.sortedDataset.lines
            : this.dataset.lines,
          function(d) {
            return d3.max(d.items, function(d2) {
              return d2.value + (d2.value * 10) / 100
            })
          },
        ),
      ])
    } else {
      this.y.domain([
        0,
        d3.max(
          this.sortedDataset.lines && this.sortedDataset.lines.length > 0
            ? this.sortedDataset.lines
            : this.dataset.lines,
          function(d) {
            return d3.max(d.items, function(d2) {
              return d2.value + (d2.value * 10) / 100
            })
          },
        ),
      ])
    }

    //Display weekends background rectangles
    this.displayWeekendBackground()

    //Draw chart's lines
    var path = this.g
      .selectAll('line')
      .data(this.sortedDataset.lines)
      .enter()
      .append('path')
      .attr('class', 'line')
      .attr('id', function(d) {
        return 'line-' + that.chartId + '-' + d.sort
      })
      .attr('stroke', function(d, i) {
        return that.getFocusColor(d, i)
      })
      .attr('d', function(d) {
        return valueline(d.items)
      })

    //Animate lines drawing
    var totalLength = []
    path['_groups'][0].forEach(el => {
      totalLength.push(el.getTotalLength())
    })

    this.createAxis()

    path['_groups'][0].forEach((p, i) => {
      d3.select(p)
        .attr('stroke-dasharray', totalLength[i] + ' ' + totalLength[i])
        .attr('stroke-dashoffset', totalLength[i])
        .transition()
        .duration(1000)
        .ease(d3.easeSin)
        .attr('stroke-dashoffset', 0)
    })

    //Add chart's areas
    this.g
      .selectAll('area')
      .data(this.sortedDataset.lines)
      .enter()
      .append('path')
      .attr('class', 'area')
      .attr('id', function(d) {
        return 'area-' + that.chartId + '-' + d.sort
      })
      .attr('fill', function(d) {
        if (that.config.sort_by == 'all') {
          return that.colors['all']
        } else {
          return that.colors[d.sort]
        }
      })
      .style('opacity', '0.3')
      .attr('d', function(d) {
        return area(d.items)
      })
  }

  /**
   * Create stacked chart
   */
  createStackedChart() {
    let that = this

    let stackedDataset = []
    let mygroups = []
    let mygroup = []

    //Format a stacked dataset use to process stacked data for stacked chart
    if (this.sortedDataset.lines && this.sortedDataset.lines.length > 0) {
      this.sortedDataset.lines.forEach((line, index) => {
        mygroups.push(line.sort)
        mygroup.push(index + 1)

        line.items.forEach(item => {
          stackedDataset.push({
            date: moment(item.date, 'YYYY-MM-DD'),
            value: item.value,
            category: line.sort,
          })
        })
      })
    } else {
      //this.dataset.ite
    }

    //Group stacked data per day
    let sumstat = d3
      .nest()
      .key(function(d) {
        return d.date
      })
      .entries(stackedDataset)

    if (this.config.display_options && this.config.display_options.stacked_mode == 'percentage') {
      sumstat.forEach(sum => {
        let total = 0
        sum.values.forEach(val => {
          total += val.value
        })

        sum.values.forEach(val => {
          val.value = total > 0 ? (100 * val.value) / total : 0
        })
      })
    }

    this.stackedData = d3
      .stack()
      .keys(mygroup)
      .value(function(d, key) {
        return d.values[key - 1].value
      })(sumstat)

    //Scaling x values
    this.x = d3
      .scaleTime()
      .domain(
        d3.extent(stackedDataset.length > 0 ? stackedDataset : that.dataset.items, function(d) {
          return d ? d.date : null
        }),
      )
      .range([0, this.width])

    //Scaling y values
    this.y = d3
      .scaleLinear()
      .domain([0, this.getYMaxValue()])
      .range([this.height, 0])

    //Display weekend background rectangles
    this.displayWeekendBackground(this.x)
    this.createAxis()

    //Draw stacked area
    this.g
      .selectAll('stack')
      .data(this.stackedData)
      .enter()
      .append('path')
      .style('fill', function(d) {
        return that.colors[mygroups[d.key - 1]]
      })
      .attr(
        'd',
        d3
          .area()
          .x(function(d) {
            return that.x(d.data.key)
          })
          .y0(function(d) {
            return that.y(d[0])
          })
          .y1(function(d) {
            return that.y(d[1])
          }),
      )
      .style('opacity', '0.8')
  }

  /**
   *
   */
  createAxis() {
    let that = this

    this.smallTickXAxis =
      this.months > 4
        ? d3.axisBottom(this.x).ticks(d3.timeWeek)
        : d3.axisBottom(this.x).ticks(d3.timeDay)
    this.indicatorXAxis =
      this.months > 4
        ? d3.axisBottom(this.x).ticks(d3.timeWeek)
        : d3.axisBottom(this.x).ticks(d3.timeDay)

    //Long period
    if (this.months > 12) {
      this.smallTickXAxis = d3.axisBottom(this.x).ticks(d3.timeWeek)
      this.indicatorXAxis = d3.axisBottom(this.x).ticks(
        d3.timeMonth.filter(function(d) {
          return d3.timeMonth.count(0, d) % 2 === 0
        }),
      )
      this.xAxis = d3
        .axisBottom(this.x)
        .ticks(
          d3.timeMonth.filter(function(d) {
            return d3.timeMonth.count(0, d) % 2 === 0
          }),
        )
        .tickFormat(d3.timeFormat(this.axisDateFormat))
        .tickSizeInner(-this.height)
        .tickSizeOuter(3)
    } else if (this.months <= 12) {
      this.smallTickXAxis = d3.axisBottom(this.x).ticks(d3.timeDay)
      this.indicatorXAxis = d3.axisBottom(this.x).ticks(
        d3.timeDay.filter(function(d) {
          return d3.timeDay.count(0, d) % that.getTickDayStep() === 0
        }),
      )
      this.xAxis = d3
        .axisBottom(this.x)
        .ticks(
          d3.timeDay.filter(function(d) {
            return d3.timeDay.count(0, d) % that.getTickDayStep() === 0
          }),
        )
        .tickFormat(d3.timeFormat(this.axisDateFormat))
        .tickSizeInner(-this.height)
        .tickSizeOuter(3)
    }

    if (this.dataset.dataType == 'average_time') {
      this.yAxis = d3.axisLeft(this.y).tickFormat(function(d) {
        const value = moment.duration(d.value, 'seconds').format('mm:ss')
        return value
      })
    }

    this.yAxis = d3
      .axisLeft(this.y)
      .ticks(this.isExpandedChart() ? 10 : 5)
      .tickSizeInner(-this.width)
      .tickSizeOuter(3)

    // Add the X Axis
    this.xAxisEl = this.g
      .append('g')
      .attr('transform', 'translate(0,' + this.height + ')')
      .attr('class', 'chart-time-axis')
      .call(this.xAxis)
      .selectAll('text')
      .attr('y', 10)
      .attr('x', 0)
      .attr('fill', function(d) {
        let day = new Date(d).getDay()
        if (day == 6 || day == 0) {
          return '#dfa579'
        } else {
          return '#feffe9'
        }
      })

    d3.selectAll('.chart-time-axis line').attr('class', 'tick-line')

    // Add indicator ticks
    this.subXAxisEl = this.g
      .append('g')
      .attr('transform', 'translate(0,' + this.height + ')')
      .attr('class', 'chart-time-axis sub-x-axis')
      .call(this.smallTickXAxis)
      .style('stroke-width', '0.5px')
      .selectAll('text')
      .remove()

    // Add indicator ticks
    this.otherSubXAxisEl = this.g
      .append('g')
      .attr('transform', 'translate(0,' + this.height + ')')
      .attr('class', 'chart-time-axis sub-x-axis')
      .call(this.indicatorXAxis)
      .style('stroke-width', '1.5px')
      .selectAll('text')
      .remove()

    d3.selectAll('.sub-x-axis line').attr('style', function(d) {
      let day = new Date(d).getDay()
      if (day == 6 || day == 0) {
        return 'stroke: #dfa579'
      } else {
        return 'stroke: #feffe9'
      }
    })

    // Add the Y Axis
    this.yAxisEl = this.g
      .append('g')
      .attr('transform', 'translate(0, 0)')
      .attr('class', 'chart-axis')
      .call(this.yAxis)
      .selectAll('line')
      .attr('class', 'tick-line')
  }

  /**
   * Create legend and display it
   */
  createLegends() {
    let legends = Array.from(this.dataset.lines, line => {
      return line.sort
    })
    legends.push('all')

    // Add legends
    if (legends.length > 2) {
      let legendColors = []
      this.dataset.lines.forEach(line => {
        if (line.sort == this.$t('others')) {
          legendColors.push(this.colors['others'])
        } else {
          legendColors.push(this.colors[line.sort])
        }
      })
      legendColors.push('#E6E6E6')
      super.createLegends(legends, legendColors)
    }
  }

  /**
   * Draw weekends rectangles in the chart's background
   * @param {*} xScale used to draw chart
   */
  displayWeekendBackground() {
    this.xSpace =
      this.dataset && this.dataset.items && this.dataset.items.length >= 2
        ? this.x(this.dataset.items[1].date) - this.x(this.dataset.items[0].date)
        : this.width
    if (this.hasToDisplayWeekEnd()) {
      let that = this
      this.weekEnds = this.dataset.items.filter(item => {
        let date = new Date(item.date)
        return date.getDay() == 0 || date.getDay() == 6
      })

      this.weekEnds = this.weekEnds.map(item => {
        return moment(item.date, 'YYYY-MM-DD')
      })

      this.g
        .selectAll('rect')
        .data(this.weekEnds)
        .enter()
        .append('rect')
        .attr('class', 'weekend-rect')
        .attr('x', function(d) {
          return that.x(d) - that.xSpace / 2
        })
        .attr('y', 0)
        .attr('width', function(d, i) {
          if (that.weekEnds[i + 1]) {
            let duration = moment.duration(that.weekEnds[i + 1].diff(d)).asDays()
            if (duration == 1) {
              return that.x(that.weekEnds[i + 1]) - that.x(d) + that.xSpace
            } else {
              return 0
            }
          } else {
            return 0
          }
        })
        .attr('height', this.height)
    }
  }

  /**
   * Set number of months for the TimeChart
   * @param {*} months
   */
  setMonths(months) {
    this.months = months
  }

  /**
   * Return tick step according to the number of days in searched period
   */
  getTickDayStep() {
    if (this.months <= 1) {
      if (this.days <= 15) {
        return 1
      } else {
        return 4
      }
    } else {
      if (this.months < 3) {
        return 4
      } else if (this.months <= 6) {
        return 10
      } else if (this.months <= 9) {
        return 15
      } else {
        return 30
      }
    }
  }

  getTickMonthStep() {
    if (this.months <= 36) {
      return 2
    } else {
      return 3
    }
  }

  /**
   * Set searched days
   * @param {*} days
   */
  setDays(days) {
    this.days = days
  }

  /**
   *
   * @param {} otherCategories
   */
  initTooltip(otherCategories) {
    super.initTooltip(otherCategories)
  }

  /**
   * Return the opacity of the current legend according to his checked status
   * @param {*} d
   */
  getLegendOpacity(d) {
    if (this.checked.indexOf(d) > -1) {
      return '1.0'
    } else {
      return '0.2'
    }
  }

  /**
   * Return if the current legend is checked or not
   * @param {*} d
   * @param {*} i
   */
  isLegendChecked(d) {
    if (this.checked.indexOf(d) > -1) {
      return true
    } else {
      return false
    }
  }

  /**
   * Click on legend listener
   */
  clickOnLegend(clickedLegend, d) {
    super.clickOnLegend(clickedLegend, d)

    let isChecked = clickedLegend.attr('checked')
    isChecked = isChecked == 'true' ? 'false' : 'true'
    clickedLegend.attr('checked', isChecked)
    clickedLegend.style('opacity', clickedLegend.attr('checked') == 'true' ? '1.0' : '0.2')

    if (isChecked == 'true') {
      if (d === 'all') {
        this.checked = Array.from(this.dataset.lines, line => {
          return line.sort
        })
        this.checked.push('all')
        this.svg.selectAll('.legendRect').style('opacity', '1.0')
        this.svg.selectAll('.legendRect').attr('checked', 'true')

        this.unchecked = []
      } else {
        this.checked.push(d)
        this.unchecked.splice(this.unchecked.indexOf(d), 1)
      }
    } else {
      if (d == 'all') {
        this.checked = []
        this.unchecked = Array.from(this.dataset.lines, line => {
          return line.sort
        })
        this.svg.selectAll('.legendRect').style('opacity', '0.2')
        this.svg.selectAll('.legendRect').attr('checked', 'false')
      } else {
        this.checked.splice(this.checked.indexOf(d), 1)
        this.unchecked.push(d)
      }
    }

    this.sortedDataset.lines = this.dataset.lines.filter(line => {
      return this.checked.indexOf(line.sort) > -1
    })

    //Re-draw chart
    this.createChart()

    this.updateTimeCharts()
  }

  /**
   *
   * @param {*} id
   */
  getFocusedTimeChart(id) {
    if (this.timeCharts) {
      let found = this.timeCharts.find(tc => {
        return tc.chartId == id
      })
      return found
    } else {
      return null
    }
  }

  /**
   * Save a reference to other displayed TimeCharts (use to save their own y scales)
   * @param {*} timeCharts
   */
  setTimeChartsRef(timeCharts) {
    this.timeCharts = timeCharts
  }

  /**
   * Update current TimeChart (useful for others TimeCharts displayed in the dashboard's page)
   */
  updateTimeCharts() {
    if (this.timeCharts) {
      let found = this.timeCharts.find(timeChart => {
        return timeChart.chartId == this.chartId
      })
      if (found) {
        found = this
      }
    }
  }

  /**
   * Return color according to value's type
   * @param {*} d
   * @param {*} i
   */
  getFocusColor(d, i) {
    if (this.isStackedChart()) {
      let category = d[0].data.values[i].category
      return this.colors[String(category)]
    } else {
      if (this.config.sort_by == 'all') {
        return this.colors['all']
      } else {
        return this.colors[d.sort]
      }
    }
  }

  /**
   * Return color for legend according to value's type
   */
  getInfosLegendColor(d) {
    if (d.sortBy == 'all') {
      return this.colors['all']
    } else {
      return this.colors[d.sort]
    }
  }

  /**
   * Return if chart is stacked
   */
  isStackedChart() {
    return this.config.display_options && this.config.display_options.stacked
  }

  /**
   * Return if chart is zoomed
   */
  isZoomedChart() {
    return this.config.display_options && this.config.display_options.zoomed
  }

  /**
   * Return if chart has to display background rectangles for week-ends
   */
  hasToDisplayWeekEnd() {
    return this.config.display_options && this.config.display_options.display_weekend
  }

  /**
   * Hide on over values when mouse out of chart
   */
  onInformationsMouseOut() {
    d3.selectAll('.mouse-line').style('opacity', '0')
    d3.selectAll('.mouse-per-line circle').style('opacity', '0')
    d3.selectAll('.mouse-informations').style('opacity', '0')
    d3.selectAll('.focused-date').style('opacity', '0')
    d3.selectAll('.focused-date-rect').style('opacity', '0')
  }

  /**
   * Display on over values for mouse event
   */
  onInformationsMouseOver() {
    d3.selectAll('.mouse-line').style('opacity', '1')
    d3.selectAll('.mouse-per-line circle').style('opacity', '1')
    d3.selectAll('.mouse-informations').style('opacity', '1')
    d3.selectAll('.focused-date').style('opacity', '1')
    d3.selectAll('.focused-date-rect').style('opacity', '0.8')
  }
}
